import React, { useId, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { INTERNAL_TASKS_KEY } from '@app/constants/queryKeys'

import { LntDrawer, LntForm } from '@app/components/base'
import { ActivityFeed, CommentsTab } from '@app/components/ActivityFeed'

import {
  getInternalTasksItemCommentsDataFromResponse,
  getInternalTasksItemDataFromResponse
} from '@app/utils/responseAdapters'

import { useNotification, useUserQuery } from '@app/hooks'

import {
  deleteInternalTasksComments,
  getInternalTasksComments,
  getInternalTasksItem,
  patchInternalTasksComments,
  patchInternalTasksItemExecutor,
  postInternalTasksComments,
  postInternalTasksItemApprove,
  postInternalTasksItemCancel,
  postInternalTasksItemReject
} from '@app/api/routes/internalTasks'

import { ACTIVITY_FEED_TABS_KEYS } from '@app/components/ActivityFeed/constants'

import { DetailsSection, DescriptionSection, DrawerFooter } from './components'

import { DEFAULT_VALUES_FORM } from './components/constants'

import { mutationFactoryHandler } from './utils'

import type { InternalTaskCardDrawerProps } from './types'
import type {
  DeleteInternalTasksCommentsData,
  PatchInternalTasksComments,
  PatchInternalTasksItemExecutorData,
  PostInternalTasksCommentsData,
  PostInternalTasksItemApproveData,
  PostInternalTasksItemCancelData,
  PostInternalTasksItemRejectData
} from '@app/api/routes/internalTasks/types'
import type { TabsProps } from 'antd'

export const InternalTaskCardDrawer: React.FC<InternalTaskCardDrawerProps> = props => {
  const { taskId, onClose, onSelf, ...restProps } = props

  const { data: userData } = useUserQuery()
  const formId = useId()
  const form = useForm<PostInternalTasksCommentsData>()

  const watchCommentMessage = form.watch('message')

  const queryClient = useQueryClient()
  const notification = useNotification()

  const { data: itemData } = useQuery({
    enabled: !!taskId,
    queryKey: [INTERNAL_TASKS_KEY.internalTasksItem],
    queryFn: async () => await getInternalTasksItem({ id: taskId ?? '' })
  })

  const userIsInitiator = useMemo(() => itemData?.initiator?.id === userData?.id, [itemData, userData])

  const { data: itemCommentsData, isLoading: isItemCommentsDataLoading } = useQuery({
    enabled: !!taskId,
    queryKey: [INTERNAL_TASKS_KEY.internalTasksItemComments],
    queryFn: async () =>
      await getInternalTasksComments({
        taskId: taskId ?? '',
        // TODO: Убрать, когда появится пагинация в UI
        limit: 100
      })
  })

  const postInternalTasksItemCancelMutation = useMutation({
    mutationFn: postInternalTasksItemCancel,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItem] })

      notification.success({
        message: 'Задача успешно отменена.'
      })
    }
  })

  const patchInternalTasksItemExecutorMutation = useMutation({
    mutationFn: patchInternalTasksItemExecutor,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItem] })

      notification.success({
        message: 'Исполнитель успешно изменен.'
      })
    }
  })

  const postInternalTasksItemApproveMutation = useMutation({
    mutationFn: postInternalTasksItemApprove,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItem] })

      notification.success({
        message: 'Успешно согласовано.'
      })

      onClose()
    }
  })

  const postInternalTasksItemRejectMutation = useMutation({
    mutationFn: postInternalTasksItemReject,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItem] })

      notification.success({
        message: 'Успешно Отклонено.'
      })

      onClose()
    }
  })

  const postInternalTasksCommentsMutation = useMutation({
    mutationFn: postInternalTasksComments,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItemComments] })

      notification.success({
        message: 'Комментарий успешно добавлен.'
      })

      form.reset(DEFAULT_VALUES_FORM)
    }
  })

  const patchInternalTasksCommentsMutation = useMutation({
    mutationFn: patchInternalTasksComments,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItemComments] })

      notification.success({
        message: 'Комментарий успешно изменен.'
      })
    }
  })

  const deleteInternalTasksCommentsMutation = useMutation({
    mutationFn: deleteInternalTasksComments,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItemComments] })

      notification.success({
        message: 'Комментарий успешно удален.'
      })
    }
  })

  const handleTasksItemCancel = (): void => {
    mutationFactoryHandler<PostInternalTasksItemCancelData>(postInternalTasksItemCancelMutation, taskId)
  }

  const handleTasksItemChangeExecutor = (executorUserId: number): void => {
    mutationFactoryHandler<PatchInternalTasksItemExecutorData>(patchInternalTasksItemExecutorMutation, taskId, {
      executorUserId
    })
  }

  const handleTasksItemApprove = (): void => {
    mutationFactoryHandler<PostInternalTasksItemApproveData>(postInternalTasksItemApproveMutation, taskId)
  }

  const handleTasksItemReject = (): void => {
    mutationFactoryHandler<PostInternalTasksItemRejectData>(postInternalTasksItemRejectMutation, taskId)
  }

  const handlePostInternalTasksComments = (data: PostInternalTasksCommentsData): void => {
    mutationFactoryHandler<PostInternalTasksCommentsData>(postInternalTasksCommentsMutation, taskId, data)
  }

  const handleDeleteItemComment = (commentId: string): void => {
    mutationFactoryHandler<DeleteInternalTasksCommentsData>(deleteInternalTasksCommentsMutation, taskId, { commentId })
  }

  const handlePatchCommentItem = (commentId: string, message: string): void => {
    mutationFactoryHandler<PatchInternalTasksComments>(patchInternalTasksCommentsMutation, taskId, {
      commentId,
      message
    })
  }

  const dataSource = getInternalTasksItemDataFromResponse(itemData)
  const commentsDataSource = getInternalTasksItemCommentsDataFromResponse(itemCommentsData)

  const activityTabsItems: TabsProps['items'] = [
    {
      key: ACTIVITY_FEED_TABS_KEYS.comments,
      label: 'Комментарии',
      children: (
        <CommentsTab
          data={ commentsDataSource }
          onDeleteComment={ handleDeleteItemComment }
          onPatchComment={ handlePatchCommentItem }
        />
      )
    },
    {
      key: ACTIVITY_FEED_TABS_KEYS.commentsHistory,
      label: 'История изменений',
      disabled: true
    }
  ]

  return (
    <LntDrawer
      { ...restProps }
      width={ 672 }
      onClose={ onClose }
      title={ dataSource?.name }
      footer={
        <DrawerFooter
          userIsInitiator={ userIsInitiator }
          formId={ formId }
          formIsValid={ !watchCommentMessage }
          isSubmittingComment={ postInternalTasksCommentsMutation.isLoading }
          onReject={ handleTasksItemReject }
          onApprove={ handleTasksItemApprove }
          onSelf={ onSelf }
          onCancel={ handleTasksItemCancel }
        />
      }
    >
      <DetailsSection
        taskId={ taskId }
        dataSource={ dataSource }
        onChangeExecutor={ handleTasksItemChangeExecutor }
      />
      <DescriptionSection dataSource={ dataSource } />

      <LntForm
        loading={ isItemCommentsDataLoading }
        layout='vertical'
        formInstance={ form }
        id={ formId }
        onSubmit={ handlePostInternalTasksComments }
      >
        <ActivityFeed
          tabsItems={ activityTabsItems }
          defaultActiveKey={ ACTIVITY_FEED_TABS_KEYS.comments }
        />
      </LntForm>
    </LntDrawer>
  )
}
